/**
 * Pure helpers for the duplicate-import guard.
 *
 * When a roster row turns out to be a player the organization already has, the
 * default is to add that player to the team being imported rather than create a
 * second record. Anything the stored record is missing gets filled in from the
 * roster automatically. Where the two actually disagree the coach has to choose,
 * and the stored value is what wins unless they say otherwise.
 *
 * This module works out which fields disagree and folds the coach's picks into
 * the `overwriteFields` list the API expects.
 */

import type { DuplicateMatch, ImportCandidate } from '../../../../types/api';
import type { ShowcaseProspectRequest } from '../../../../types/extraction';

/** What to do with one flagged roster row. */
export type ImportDecision =
  /** Add the existing player to this team and merge the row into their record. */
  | 'merge'
  /** Create a separate record; the coach can merge them later. */
  | 'import_as_new'
  /** Leave the row out of the import entirely. */
  | 'skip';

/** The default. Re-importing a roster should update the players you have, not skip them. */
export const DEFAULT_IMPORT_DECISION: ImportDecision = 'merge';

/**
 * Comparable fields, in the order the guard lists them. `key` is the camelCase
 * name the API accepts in `overwriteFields`, and it is also the property name on
 * both {@link ImportCandidate} and {@link DuplicateMatch}.
 */
const COMPARABLE_FIELDS: { key: string; label: string }[] = [
  { key: 'position', label: 'Position' },
  { key: 'gradYear', label: 'Grad year' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'height', label: 'Height' },
  { key: 'weight', label: 'Weight' },
  { key: 'bats', label: 'Bats' },
  { key: 'throws', label: 'Throws' },
];

/**
 * Fields the guard must not raise a question about, even when both sides hold a real value.
 *
 * Passed by a caller whose JUCO toggle is on. A stored `HighSchool` beside an incoming `JUCO` is
 * two non-blank values that disagree, which is exactly what {@link computeImportConflicts} counts
 * — so without this a re-import of a JUCO roster would ask the coach to confirm the toggle they
 * just flipped, on every row, on every import. The toggle already IS the answer: the API
 * overwrites these two however the row matched (`ProspectFieldHelper.RosterClassificationFields`).
 *
 * `COMPARABLE_FIELDS` does not list them today, so nothing fires yet. This is deliberately a
 * standing suppression rather than a comment: adding either field to the comparable list later
 * would otherwise reintroduce the card silently.
 */
export type ConflictSuppression = {
  /** camelCase field names to leave out of both the conflict list and the auto-fill list. */
  suppressedFields?: readonly string[];
};

/** The comparable fields left after suppression. */
const comparableFields = (options?: ConflictSuppression) => {
  const suppressed = options?.suppressedFields;
  if (!suppressed || suppressed.length === 0) return COMPARABLE_FIELDS;
  return COMPARABLE_FIELDS.filter((field) => !suppressed.includes(field.key));
};

/**
 * Reduce an outgoing player to the fields the duplicate check compares on. Sent
 * for every row, so the flags come back with enough on both sides to diff.
 */
export const toImportCandidate = (player: ShowcaseProspectRequest): ImportCandidate => ({
  firstName: player.firstName,
  lastName: player.lastName,
  gradYear: player.gradYear ?? null,
  position: player.position ?? null,
  email: player.email ?? null,
  phone: player.phone ?? null,
  height: player.height ?? null,
  weight: player.weight ?? null,
  bats: player.bats ?? null,
  throws: player.throws ?? null,
});

/** A field where the roster row and the stored record hold different real values. */
export type ImportFieldConflict = {
  /** camelCase field name, sent back in `overwriteFields` when the roster value is picked. */
  key: string;
  label: string;
  /** What the organization already has on file. */
  existingValue: string | number;
  /** What this roster row says. */
  incomingValue: string | number;
};

/** Normalize a field value so blanks compare equal and stray whitespace does not matter. */
const normalize = (value: unknown): string | number | null => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const trimmed = String(value).trim();
  return trimmed.length === 0 ? null : trimmed;
};

/** Loose equality for conflict purposes: "12" and 12 are the same value. */
const valuesEqual = (a: string | number | null, b: string | number | null): boolean => {
  if (a === null || b === null) return a === b;
  return String(a).toLowerCase() === String(b).toLowerCase();
};

/**
 * Phones compare by their digits.
 *
 * One number is written a dozen ways, and the two sides of this comparison come from different
 * places: the roster row arrives normalized ("859-609-3174") while the stored value may be bare
 * digits, whatever an older import kept, or whatever a coach typed. Comparing the text asks the
 * coach to resolve a conflict between two spellings of the same number, on every matched row of
 * a re-imported roster.
 */
const phoneDigits = (value: string | number): string => {
  const digits = String(value).replace(/\D/g, '');
  // Same rule as sanitizePhone: a leading US country code is not part of the number.
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
};

const fieldValuesEqual = (
  key: string,
  a: string | number | null,
  b: string | number | null,
): boolean => {
  if (a === null || b === null) return a === b;
  if (key === 'phone') return phoneDigits(a) === phoneDigits(b);
  return valuesEqual(a, b);
};

/**
 * Fields where the roster row and the matched record disagree and someone has to
 * choose.
 *
 * A field only counts as a conflict when BOTH sides carry a real value. If the
 * stored record is blank the roster's value is filled in automatically by the
 * import, and if the roster is blank there is nothing to apply, so neither case
 * is worth a question. Fields named in `options.suppressedFields` are never a
 * question either — see {@link ConflictSuppression}.
 */
export const computeImportConflicts = (
  candidate: ImportCandidate,
  match: DuplicateMatch,
  options?: ConflictSuppression
): ImportFieldConflict[] => {
  const conflicts: ImportFieldConflict[] = [];

  for (const { key, label } of comparableFields(options)) {
    const incoming = normalize((candidate as unknown as Record<string, unknown>)[key]);
    const existing = normalize((match as unknown as Record<string, unknown>)[key]);

    if (incoming === null || existing === null) continue;
    if (fieldValuesEqual(key, incoming, existing)) continue;

    conflicts.push({ key, label, existingValue: existing, incomingValue: incoming });
  }

  return conflicts;
};

/**
 * Fields the stored record is missing that this roster row can supply. Shown as
 * a plain list because the import applies them with no decision needed.
 */
export const computeImportFills = (
  candidate: ImportCandidate,
  match: DuplicateMatch,
  options?: ConflictSuppression
): { key: string; label: string; value: string | number }[] => {
  const fills: { key: string; label: string; value: string | number }[] = [];

  for (const { key, label } of comparableFields(options)) {
    const incoming = normalize((candidate as unknown as Record<string, unknown>)[key]);
    const existing = normalize((match as unknown as Record<string, unknown>)[key]);

    if (incoming !== null && existing === null) {
      fills.push({ key, label, value: incoming });
    }
  }

  return fills;
};

/** Which side of a conflict the coach picked. */
export type ConflictChoice = 'existing' | 'incoming';

/** Per-field picks for one flagged row, keyed by field name. Absent means "existing". */
export type ConflictChoices = Record<string, ConflictChoice>;

/**
 * The field names to send as `overwriteFields` — only the ones the coach moved
 * off the stored value. Everything else needs no write.
 */
export const buildOverwriteFields = (
  conflicts: ImportFieldConflict[],
  choices: ConflictChoices
): string[] => conflicts.filter((c) => choices[c.key] === 'incoming').map((c) => c.key);

/** Render a value for display. */
export const displayValue = (value: string | number | null | undefined): string => {
  const normalized = normalize(value);
  return normalized === null ? 'Not set' : String(normalized);
};
